import React, { useState, useEffect, useMemo, useRef } from "react";
import DashboardLayout from "../DashboardLayout";
import apiClient from "../../utils/apiClient";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../components/ui/dialog";
import RSCChart from "./RSCChart";
import RankingsTable from "./RankingsTable";
import ETFIntelligence from "./ETFIntelligence";
import { Loader2, RefreshCw, AlertCircle, Menu, Maximize2 } from "lucide-react";
import MissingStocksTable from "./MissingStocksTable";
import moment from "moment";
import server from "../../utils/serverConfig";
import { TradingViewChartsPanel } from "./TradingViewMiniChart";

const API = server.godseye.baseUrl.replace(/\/$/, ""); // Remove trailing slash if any

const RelativeStrength = () => {
    const [config, setConfig] = useState(null);
    const [activeTab, setActiveTab] = useState("India");
    const [activeGroup, setActiveGroup] = useState(null);
    const [drillDownIndex, setDrillDownIndex] = useState(null); 
    const matrixRef = useRef(null);
    const [chartData, setChartData] = useState([]);
    const [rankings, setRankings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [rankLoading, setRankLoading] = useState(false);
    const [duration, setDuration] = useState("1Y"); 
    const [endDate] = useState(moment().format('YYYY-MM-DD'));
    const [mainTab, setMainTab] = useState("RS");
    const [syncingState, setSyncingState] = useState({ india: false, global: false, etf: false, stocks: false, commodities: false });
    const [missingStocks, setMissingStocks] = useState([]);
    const [missingLoading, setMissingLoading] = useState(false);
    const [showMissingStocks, setShowMissingStocks] = useState(false);
    const [pinnedSymbols, setPinnedSymbols] = useState([]);
    
    // Resizability toggle
    const [isResizable, setIsResizable] = useState(() => {
        try {
            return localStorage.getItem('rs_dashboard_resizable') === 'true';
        } catch(e) { return false; }
    });

    const toggleResizable = () => {
        setIsResizable(prev => {
            const next = !prev;
            localStorage.setItem('rs_dashboard_resizable', next.toString());
            return next;
        });
    };
    
    // Resize dimensions
    const [containerHeights, setContainerHeights] = useState(() => {
        try {
            const saved = localStorage.getItem('rs_dashboard_heights');
            if (saved) return JSON.parse(saved);
        } catch(e) {}
        return { rs: 800, tv: 836 };
    });
    const rsContainerRef = useRef(null);
    const tvContainerRef = useRef(null);
    const heightsRef = useRef(containerHeights);

    useEffect(() => {
        let timeoutId;
        const observer = new ResizeObserver((entries) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                let updated = false;
                let newHeights = { ...heightsRef.current };
                
                for (let entry of entries) {
                    if (entry.target === rsContainerRef.current) {
                        const h = entry.borderBoxSize ? entry.borderBoxSize[0].blockSize : entry.contentRect.height;
                        if (Math.abs(h - newHeights.rs) > 5) {
                            newHeights.rs = Math.round(h);
                            updated = true;
                        }
                    }
                    if (entry.target === tvContainerRef.current) {
                        const h = entry.borderBoxSize ? entry.borderBoxSize[0].blockSize : entry.contentRect.height;
                        if (Math.abs(h - newHeights.tv) > 5) {
                            newHeights.tv = Math.round(h);
                            updated = true;
                        }
                    }
                }

                if (updated) {
                    heightsRef.current = newHeights;
                    setContainerHeights(newHeights);
                    localStorage.setItem('rs_dashboard_heights', JSON.stringify(newHeights));
                }
            }, 300);
        });

        if (rsContainerRef.current) observer.observe(rsContainerRef.current);
        if (tvContainerRef.current) observer.observe(tvContainerRef.current);

        return () => {
            observer.disconnect();
            clearTimeout(timeoutId);
        };
    }, []);

    const lastDataDate = useMemo(() => {
        if (!chartData || chartData.length === 0) return null;
        let latest = null;
        chartData.forEach(series => {
            if (series.x && series.x.length > 0) {
                const seriesLatest = series.x[series.x.length - 1];
                if (!latest || moment(seriesLatest).isAfter(moment(latest))) {
                    latest = seriesLatest;
                }
            }
        });
        return latest ? moment(latest).format('DD MMM YYYY') : null;
    }, [chartData]);

    // Color map derived from chart data (mirrors RSCChart PALETTE)
    const PALETTE = [
        "#e6194b", "#3cb44b", "#4363d8", "#f58231", "#911eb4",
        "#42d4f4", "#f032e6", "#bfef45", "#fabed4", "#469990",
        "#dcbeff", "#9A6324", "#fffac8", "#800000", "#aaffc3",
        "#808000", "#ffd8b1", "#000075", "#a9a9a9", "#e6beff",
        "#ffe119", "#00ff7f", "#ff6347", "#7b68ee", "#00ced1",
        "#ff1493", "#ffa07a", "#98fb98", "#dda0dd", "#87ceeb",
        "#cd853f", "#20b2aa", "#ff4500", "#1e90ff", "#ffffff",
    ];
    const chartColorMap = useMemo(() => {
        const map = {};
        if (chartData) {
            chartData.forEach((series, idx) => {
                map[series.name] = PALETTE[idx % PALETTE.length];
            });
        }
        return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartData]);

    const startDate = useMemo(() => {
        if (duration === "1M") return moment().subtract(1, 'month').format('YYYY-MM-DD');
        if (duration === "3M") return moment().subtract(3, 'months').format('YYYY-MM-DD');
        if (duration === "6M") return moment().subtract(6, 'months').format('YYYY-MM-DD');
        if (duration === "9M") return moment().subtract(9, 'months').format('YYYY-MM-DD');
        if (duration === "1Y") return moment().subtract(1, 'year').format('YYYY-MM-DD');
        if (duration === "ALL") return null; // Fetch all data
        return moment().subtract(1, 'year').format('YYYY-MM-DD');
    }, [duration]);

    const handleSync = async (action) => {
        setSyncingState(prev => ({ ...prev, [action]: true }));
        try {
            await apiClient.post(`${API}/rsc/refresh/${action}`);
            // Success - keep spinner for a few seconds purely for visual confirmation 
            // since backend is async anyway
            setTimeout(() => {
                setSyncingState(prev => ({ ...prev, [action]: false }));
            }, 3000);
        } catch (err) {
            console.error(`Sync ${action} failed`, err);
            setSyncingState(prev => ({ ...prev, [action]: false }));
        }
    };

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const res = await apiClient.get(`${API}/rsc/config`);
                setConfig(res.data);
            } catch (err) {
                console.error("Config fetch failed", err);
            }
        };
        fetchConfig();
    }, []);

    // Reset group when tab changes
    useEffect(() => {
        if (config && config.countries[activeTab]) {
            setActiveGroup(config.countries[activeTab].groups[0]);
        } else {
            setActiveGroup(null);
        }
    }, [activeTab, config]);

    // Fetch chart data
    useEffect(() => {
        if (activeTab === "ETF Intelligence" || !config) return;
        
        // Safety: Don't fetch until the group is properly initialized for countries that have groups
        const isGlobal = activeTab === "Global";
        const isCommodities = activeTab === "Commodities";
        if (!isGlobal && !isCommodities && !activeGroup && !drillDownIndex) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                if (isCommodities) {
                    const res = await apiClient.get(`${API}/rsc/commodities/prices`, {
                        params: { start_date: startDate, end_date: endDate }
                    });
                    // Transform OHLC to { x, y }
                    const formatted = (Array.isArray(res.data) ? res.data : []).map(c => ({
                        name: c.name,
                        x: c.time,
                        y: c.close
                    }));
                    setChartData(formatted);
                } else {
                    const params = { 
                        country: activeTab, 
                        start_date: startDate, 
                        end_date: endDate 
                    };
                    if (drillDownIndex) {
                        params.index_name = drillDownIndex;
                    } else if (!isGlobal) {
                        params.group = activeGroup;
                    }

                    const res = await apiClient.get(`${API}/rsc/chart`, { params });
                    setChartData(Array.isArray(res.data) ? res.data : []);
                }
            } catch (err) {
                console.error("Chart fetch failed", err);
                setChartData([]);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [activeTab, activeGroup, drillDownIndex, startDate, endDate, config]);

    // Fetch rankings data independently
    useEffect(() => {
        if (activeTab === "ETF Intelligence" || !config) return;
        
        // Safety: Don't fetch until the group is properly initialized for countries that have groups
        const isGlobal = activeTab === "Global";
        const isCommodities = activeTab === "Commodities";
        if (!isGlobal && !isCommodities && !activeGroup && !drillDownIndex) return;

        const fetchRankings = async () => {
            setRankLoading(true);
            try {
                if (isCommodities) {
                    const res = await apiClient.get(`${API}/rsc/commodities/rankings`, {
                        params: { start_date: startDate, end_date: endDate }
                    });
                    const data = Array.isArray(res.data) ? res.data : [];
                    // Map current_price/change_pct to a format RankingsTable can use
                    const mapped = data.map(r => ({
                        ...r,
                        current_rsc: r.change_pct // Use change_pct for the main value column
                    }));
                    setRankings(mapped);
                } else {
                    const params = { 
                        country: activeTab, 
                        start_date: startDate, 
                        end_date: endDate 
                    };
                    if (drillDownIndex) {
                        params.index_name = drillDownIndex;
                    } else if (!isGlobal) {
                        params.group = activeGroup;
                    }

                    const res = await apiClient.get(`${API}/rsc/rankings`, { params });
                    let rankingsData = Array.isArray(res.data) ? res.data : [];

                    // Inject benchmark/baseline into rankings if not present
                    const benchmarkName = drillDownIndex || (activeTab !== "Global" ? config.countries[activeTab]?.benchmark : null);
                    
                    if (benchmarkName && !rankingsData.some(r => r.name === benchmarkName)) {
                        rankingsData.push({
                            name: benchmarkName,
                            current_rsc: 1.0,
                            '1W': 0,
                            '1M': 0,
                            '3M': 0,
                            '6M': 0,
                            '1Y': 0,
                            trend: 'neutral',
                            sparkline: new Array(10).fill(1)
                        });
                    }

                    setRankings(rankingsData);
                }
            } catch (err) {
                console.error("Rankings fetch failed", err);
                setRankings([]);
            } finally {
                setRankLoading(false);
            }
        };
        fetchRankings();
    }, [activeTab, activeGroup, drillDownIndex, startDate, endDate, config]);

    // Fetch missing stocks
    useEffect(() => {
        if (!showMissingStocks) return;
        const fetchMissing = async () => {
            setMissingLoading(true);
            try {
                const res = await apiClient.get(`${API}/rsc/missing-stocks`);
                setMissingStocks(res.data);
            } catch (err) {
                console.error("Missing stocks fetch failed", err);
            } finally {
                setMissingLoading(false);
            }
        };
        fetchMissing();
    }, [showMissingStocks]);

    const handleRowClick = (name) => {
        // If name matches a country exactly, switch to that country instead of drill down
        if (config && config.countries[name]) {
            setActiveTab(name);
            setDrillDownIndex(null);
        } else {
            setDrillDownIndex(name);
        }

        // Smooth scroll to the matrix section
        setTimeout(() => {
            matrixRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
    };

    const resetDrillDown = () => setDrillDownIndex(null);

    if (!config) return (
        <div className="flex h-screen items-center justify-center bg-zinc-950">
            <Loader2 className="animate-spin text-emerald-500 h-8 w-8" />
        </div>
    );

    const countryKeys = ["Commodities", "Global", ...Object.keys(config.countries)];

    const chartTitle = (() => {
        if (drillDownIndex) return `${drillDownIndex} Constituents — RS vs ${drillDownIndex}`;
        if (activeTab === "Global") return "Global — Benchmarks vs 6% Bond Rate";
        if (activeTab === "Commodities") return "Commodities — Futures Price Performance Across Multiple Expanses";
        const group = activeGroup || "All";
        const benchmark = config.countries[activeTab]?.benchmark || "";
        return `${activeTab} — ${group} — Relative Strength vs ${benchmark}`;
    })();

    return (
        <DashboardLayout title="Relative Strength Analytics">
            <div className="flex flex-col w-full min-h-screen bg-[#09090b] p-2 space-y-2">
                {/* Main View Selector (RS vs ETF) & Sync Controls */}
                <div className="flex flex-col md:flex-row items-center justify-between border-b border-zinc-900 pb-2 gap-2">
                    <div className="flex-1" /> {/* Spacer */}
                    
                    <Tabs value={mainTab} onValueChange={setMainTab} className="w-80 lg:w-96">
                        <TabsList className="grid w-full grid-cols-2 bg-zinc-900 border border-zinc-800 p-1 rounded-xl shadow-2xl h-11">
                            <TabsTrigger 
                                value="RS" 
                                className="text-[13px] font-black uppercase tracking-widest data-[state=active]:bg-emerald-500 data-[state=active]:text-white transition-all rounded-lg"
                            >
                                RS Analytics
                            </TabsTrigger>
                            <TabsTrigger 
                                value="ETF" 
                                className="text-[13px] font-black uppercase tracking-widest data-[state=active]:bg-emerald-500 data-[state=active]:text-white transition-all rounded-lg"
                            >
                                ETF Intelligence
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>

                    <div className="flex-1 flex justify-end items-center gap-3">
                        <Dialog open={showMissingStocks} onOpenChange={setShowMissingStocks}>
                            {/* Dropdown Container */}
                            <div className="relative group">
                                <Button variant="outline" size="sm" className="h-11 w-11 p-0 border-zinc-800 bg-zinc-900/80 hover:bg-zinc-800 text-zinc-300 transition-all rounded-xl shadow-lg">
                                    <Menu className="h-5 w-5" />
                                </Button>
                                
                                {/* Dropdown Menu */}
                                <div className="absolute right-0 top-full mt-2 w-52 bg-zinc-950 border border-zinc-800 rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[100] flex flex-col p-2 gap-1 pointer-events-none group-hover:pointer-events-auto origin-top-right scale-95 group-hover:scale-100">
                                    <DialogTrigger asChild>
                                        <Button 
                                            variant="ghost" 
                                            size="sm"
                                            className="w-full justify-start h-9 text-[11px] font-black uppercase tracking-[0.15em] hover:bg-rose-500/10 hover:text-rose-500 text-zinc-400 transition-all"
                                        >
                                            <AlertCircle className="mr-2 h-3 w-3" />
                                            Missing Stocks
                                        </Button>
                                    </DialogTrigger>
                                    
                                    <div className="h-px w-full bg-zinc-800/80 my-1" />

                                    <Button 
                                        variant="ghost" 
                                        size="sm"
                                        className="w-full justify-start h-9 text-[11px] font-black uppercase tracking-[0.15em] hover:bg-blue-500/10 hover:text-blue-400 text-zinc-400 transition-all"
                                        onClick={toggleResizable}
                                    >
                                        <Maximize2 className={`mr-2 h-3 w-3 ${isResizable ? 'text-blue-400' : ''}`} />
                                        {isResizable ? "Lock Panels" : "Unlock Resize"}
                                    </Button>

                                    <div className="h-px w-full bg-zinc-800/80 my-1" />

                                    {[
                                        { label: 'India', action: 'india' },
                                        { label: 'Global', action: 'global' },
                                        { label: 'Commodities', action: 'commodities' },
                                        { label: 'Stocks', action: 'stocks' },
                                        { label: 'ETF', action: 'etf' }
                                    ].map((s) => (
                                        <Button 
                                            key={s.label}
                                            variant="ghost" 
                                            size="sm" 
                                            disabled={syncingState[s.action]}
                                            className="w-full justify-start h-9 text-[11px] font-black uppercase tracking-[0.15em] hover:bg-emerald-500/10 hover:text-emerald-500 text-zinc-400 transition-all" 
                                            onClick={() => handleSync(s.action)}
                                        >
                                            {syncingState[s.action] ? (
                                                <Loader2 className="mr-2 h-3 w-3 animate-spin text-emerald-500" />
                                            ) : (
                                                <RefreshCw className="mr-2 h-3 w-3" />
                                            )}
                                            {syncingState[s.action] ? "Syncing..." : `Sync ${s.label}`}
                                        </Button>
                                    ))}
                                </div>
                            </div>

                            <DialogContent className="max-w-5xl bg-zinc-950 border-zinc-900 shadow-2xl">
                                <DialogHeader className="border-b border-zinc-900 pb-4 mb-4">
                                    <div className="flex items-center gap-3">
                                        <AlertCircle className="text-rose-500 w-5 h-5" />
                                        <DialogTitle className="text-2xl font-black text-white uppercase tracking-tighter">
                                            Stock Fetch Failures <span className="text-rose-500">Review</span>
                                        </DialogTitle>
                                    </div>
                                    <p className="text-[12px] text-zinc-500 font-bold uppercase tracking-widest mt-1 text-left">
                                        Identify symbols that failed to sync during the last update cycle
                                    </p>
                                </DialogHeader>
                                <div className="max-h-[60vh] overflow-y-auto custom-scroll">
                                    <MissingStocksTable data={missingStocks} loading={missingLoading} />
                                </div>
                            </DialogContent>
                        </Dialog>
                    </div>
                </div>

                {mainTab === "ETF" ? (
                    <div className="animate-in fade-in zoom-in-95 duration-500">
                        <ETFIntelligence />
                    </div>
                ) : (
                    <div className="space-y-2 animate-in fade-in slide-in-from-left-4 duration-500">
                        {/* RS Analytics Header - Compact 1-liner */}
                        <div className="bg-zinc-900/40 py-1.5 px-3 rounded-xl border border-zinc-800/50 shadow-lg mb-2">
                            <div className="flex flex-col lg:flex-row items-center justify-start gap-4 w-full">
                                
                                {/* Title and Date */}
                                <div className="flex items-center gap-4 shrink-0">
                                    {drillDownIndex ? (
                                        <div className="flex items-center gap-3 shrink-0">
                                            <button 
                                                onClick={resetDrillDown}
                                                className="flex items-center gap-1.5 px-3 py-1 bg-zinc-900 hover:bg-zinc-800 text-white font-black text-[11px] uppercase rounded-lg border border-zinc-800 transition-all shadow-lg active:scale-95 group shrink-0"
                                            >
                                                <span className="group-hover:-translate-x-1 transition-transform">←</span>
                                                Back
                                            </button>
                                            <div className="flex items-center gap-3 justify-center">
                                                <h1 className="text-lg md:text-xl font-black text-white uppercase tracking-tighter whitespace-nowrap">
                                                    {drillDownIndex} <span className="text-emerald-500">Constituents</span>
                                                </h1>
                                                {lastDataDate && (
                                                    <div className="flex items-center gap-1 px-2 py-0.5 bg-black/20 rounded border border-white/5 w-fit">
                                                        <span className="text-[10px] text-zinc-500 font-bold uppercase tracking-[0.1em]">Updated:</span>
                                                        <span className="text-[11px] text-zinc-300 font-black uppercase tracking-tight">{lastDataDate}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-3 shrink-0">
                                            <div className="flex items-center gap-3 justify-center">
                                                <h1 className="text-lg md:text-xl font-black text-white uppercase tracking-tighter flex items-center gap-2 whitespace-nowrap">
                                                    <span className="bg-emerald-500 w-1.5 h-4 md:h-5 rounded-full"></span>
                                                    {activeTab === "Global" ? activeTab : activeTab} <span className="text-emerald-500">RS</span>
                                                </h1>
                                                {lastDataDate && (
                                                    <div className="flex items-center gap-1 px-2 py-0.5 bg-black/20 rounded border border-white/5 w-fit">
                                                        <span className="text-[10px] text-zinc-500 font-bold uppercase tracking-[0.1em]">Updated:</span>
                                                        <span className="text-[11px] text-zinc-300 font-black uppercase tracking-tight">{lastDataDate}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Dropdown (Next to Title) */}
                                {!drillDownIndex && (
                                    <div className="flex-shrink-0">
                                        <select
                                            value={activeTab}
                                            onChange={(e) => { setActiveTab(e.target.value); setDrillDownIndex(null); }}
                                            className="bg-zinc-900/80 border border-zinc-700 text-emerald-400 text-[13px] font-black uppercase px-4 py-1.5 rounded-lg outline-none cursor-pointer hover:bg-zinc-800 hover:border-emerald-500/50 transition-all shadow-lg focus:ring-2 focus:ring-emerald-500/20"
                                        >
                                            {countryKeys.map(c => (
                                                <option key={c} value={c} className="bg-zinc-950 text-white">{c}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {/* Sub-Group Tabs (Next to Dropdown) */}
                                {!drillDownIndex && activeTab !== "Global" && config.countries[activeTab] && (
                                    <div className="flex-1 min-w-0 flex items-center">
                                        <Tabs value={activeGroup} onValueChange={setActiveGroup} className="w-full">
                                            <TabsList className="bg-transparent border-0 h-8 p-0 flex overflow-x-auto whitespace-nowrap custom-scroll gap-2 justify-start items-center">
                                                {config.countries[activeTab].groups.map(g => (
                                                    <TabsTrigger 
                                                        key={g} 
                                                        value={g} 
                                                        className="text-[12px] font-black uppercase px-4 h-7 tracking-tighter data-[state=active]:bg-emerald-500 data-[state=active]:text-white text-white hover:bg-zinc-800 hover:text-white rounded-lg transition-all border border-zinc-800/50 data-[state=active]:border-emerald-500 shadow-sm"
                                                    >
                                                        {g}
                                                    </TabsTrigger>
                                                ))}
                                            </TabsList>
                                        </Tabs>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className={`flex flex-col ${pinnedSymbols.length > 0 ? 'xl:flex-row' : ''} gap-2`}>
                            {/* Chart Area */}
                            <div className={`bg-black/20 border border-zinc-800/40 rounded-xl p-3 shadow-2xl relative overflow-hidden group transition-all duration-500 ${pinnedSymbols.length > 0 ? 'xl:w-1/2' : 'w-full'}`}>
                                <div className="absolute top-0 left-0 w-1 h-full bg-emerald-500/20 group-hover:bg-emerald-500/50 transition-all" />
                                <div className="flex justify-between items-center mb-3">
                                    <h2 className="text-[13px] ml-2 text-white font-black uppercase tracking-[0.2em]">{chartTitle}</h2>
                                    {/* Timeframe Selector */}
                                    <div className="flex bg-zinc-950 border border-zinc-900 rounded-lg p-1 shadow-inner shrink-0">
                                        {["1M", "3M", "6M", "9M", "1Y", "ALL"].map((t) => (
                                            <button
                                                key={t}
                                                onClick={() => setDuration(t)}
                                                className={`px-4 py-2 text-[14px] font-black uppercase tracking-tighter rounded-md transition-all ${
                                                    duration === t 
                                                    ? "bg-emerald-500 text-white shadow-[0_0_10px_rgba(16,185,129,0.4)]" 
                                                    : "text-white hover:bg-zinc-900"
                                                }`}
                                            >
                                                {t}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div 
                                    ref={rsContainerRef}
                                    className={`transition-all duration-200 ${isResizable ? 'resize-y overflow-hidden' : 'overflow-hidden'}`}
                                    style={{ height: containerHeights.rs, minHeight: '300px' }}
                                >
                                    {loading ? (
                                        <div className="flex h-full items-center justify-center">
                                            <Loader2 className="animate-spin text-emerald-500 h-8 w-8" />
                                        </div>
                                    ) : (
                                        <RSCChart 
                                            data={chartData} 
                                            onLineClick={drillDownIndex ? null : handleRowClick}
                                            onPinnedChange={setPinnedSymbols}
                                            pinnedSymbols={pinnedSymbols}
                                            isResizable={isResizable}
                                        />
                                    )}
                                </div>
                            </div>

                            {/* TradingView Pinned Charts Panel */}
                            {pinnedSymbols.length > 0 && activeTab !== "Global" && (
                                <div 
                                    ref={tvContainerRef}
                                    className={`bg-zinc-950/80 border border-zinc-800/60 rounded-xl p-3 shadow-2xl animate-in fade-in slide-in-from-right-4 duration-500 xl:w-1/2 flex flex-col ${isResizable ? 'resize-y overflow-hidden' : 'overflow-hidden'}`}
                                    style={{ height: containerHeights.tv, minHeight: '300px' }}
                                >
                                    <div className="flex-1 overflow-y-auto custom-scroll pr-2 h-full">
                                        <TradingViewChartsPanel
                                            pinnedSymbols={pinnedSymbols}
                                            colorMap={chartColorMap}
                                            onUnpin={(name) => setPinnedSymbols(prev => prev.filter(s => s !== name))}
                                            onUnpinAll={() => setPinnedSymbols([])}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Performance Matrix */}
                        <div ref={matrixRef} className="bg-zinc-950/50 rounded-2xl border border-zinc-900 overflow-hidden shadow-2xl">
                            <div className="p-4 border-b border-zinc-900 flex justify-between items-center bg-zinc-900/20">
                                <div className="flex flex-col">
                                    <h2 className="text-xl font-black text-white uppercase tracking-tighter">
                                        {activeTab === "Commodities" ? "Commodity Performance Matrix" : (drillDownIndex ? `${drillDownIndex} Stocks` : `${activeTab} Rankings`) + " & Performance Matrix"}
                                    </h2>
                                    <p className="text-[12px] text-zinc-400 font-bold uppercase tracking-widest">
                                        Performance of {activeTab === "Commodities" ? "Futures Contracts" : (drillDownIndex ? "Constituents" : "Indices")} Across {duration === "ALL" ? "All Time" : `the Last ${duration}`}
                                    </p>
                                </div>
                                <div className="h-6 w-1 bg-emerald-500 rounded-full"></div>
                            </div>
                            <div className="p-2 overflow-x-auto min-h-[400px]">
                                {rankLoading ? (
                                    <div className="flex flex-col items-center justify-center p-20 gap-4">
                                        <div className="w-12 h-12 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin"></div>
                                        <span className="text-zinc-500 font-black text-sm uppercase tracking-widest animate-pulse">Analyzing Rankings...</span>
                                    </div>
                                ) : (
                                    <RankingsTable 
                                        data={rankings} 
                                        onRowClick={activeTab === "Commodities" || drillDownIndex ? null : handleRowClick} 
                                        nameLabel={activeTab === "Commodities" ? "Commodity" : (drillDownIndex ? "Stock" : "Index")}
                                        valueLabel={activeTab === "Commodities" ? "Perf %" : `${duration} RS`}
                                        showAsPercent={activeTab === "Commodities"}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default RelativeStrength;
